import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface FloorTypeInfo {
  id: number;
  name: string;
  area: string;
  image_url: string | null;
}

export default function PropertyStatus() {
  const navigate = useNavigate();
  const [assignedFloor, setAssignedFloor] = useState<FloorTypeInfo | null>(null);
  const [allFloorTypes, setAllFloorTypes] = useState<FloorTypeInfo[]>([]);
  const [selectedView, setSelectedView] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [houseNumber, setHouseNumber] = useState('');
  const [certImageUrl, setCertImageUrl] = useState<string | null>(null);
  const [certMovein, setCertMovein] = useState<string | null>(null);
  const [showCert, setShowCert] = useState(false);
  const [showMovein, setShowMovein] = useState(false);
  const apiBase = ((import.meta as any).env?.VITE_NEXT_PUBLIC_IMAGE_URL ?? '') as string;

  const [name, setName] = useState('');
  const [isTeamLeader, setIsTeamLeader] = useState('');
  const [propertyInfo, setPropertyInfo] = useState('');
  const [needReactivation, setNeedReactivation] = useState(true);
  const [needCompensation, setNeedCompensation] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    api.get('/housing-application/status').then(({ data }) => {
      setHasApplied(!!data.has_applied);
      const app = data.application;
      if (app) {
        setName(app.name ?? '');
        setIsTeamLeader(app.is_team_leader ? '是' : '否');
        setPropertyInfo(app.property_info ?? '');
        setNeedReactivation(app.need_reactivation ?? true);
        setNeedCompensation(app.need_compensation ?? true);
        setHouseNumber(app.house_number ?? '');
        setCertImageUrl(app.cert_image_url ?? null);
        setCertMovein(app.cert_movein ?? null);
      }
      const ft = app?.floor_type || data.eligible_floor_type || null;
      if (ft) setAssignedFloor(ft);
    }).catch(() => {});
    api.get('/me').then(({ data }) => {
      if (!name && data?.name) setName(data.name);
    }).catch(() => {});
  }, []);

  function openFloorModal() {
    if (allFloorTypes.length === 0) {
      api.get('/floor-types').then(({ data }) => {
        setAllFloorTypes(data);
        setSelectedView(assignedFloor?.id ?? (data.length > 0 ? data[0].id : null));
      }).catch(() => {});
    } else {
      setSelectedView(assignedFloor?.id ?? (allFloorTypes.length > 0 ? allFloorTypes[0].id : null));
    }
    setShowModal(true);
  }

  async function handleSubmit() {
    if (submitting) return;
    if (!name.trim()) { toast.error('请输入本人姓名'); return; }
    if (!isTeamLeader) { toast.error('请选择是否团队长'); return; }
    if (!propertyInfo.trim()) { toast.error('请输入购买的房产信息'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/housing-application', {
        name: name.trim(),
        is_team_leader: isTeamLeader === '是',
        property_info: propertyInfo.trim(),
        need_reactivation: needReactivation,
        need_compensation: needCompensation,
      });
      toast.success(data?.message ?? '提交成功');
      setHasApplied(true);
      if (data?.house_number) setHouseNumber(data.house_number);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '提交失败');
    } finally { setSubmitting(false); }
  }

  const viewingType = allFloorTypes.find(f => f.id === selectedView);

  const resolveImg = (u: string | null) => {
    if (!u) return null;
    if (/^https?:\/\//.test(u)) return u;
    if (u.startsWith('/')) {
      try { const origin = new URL(apiBase).origin; return `${origin}${u}`; } catch { return u; }
    }
    return u;
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gradient-to-b from-[#8b0000] to-[#6b0000]">
      <AuthGuard />

      {/* Hero header */}
      <div className="relative">
        <img src="/images/buban_shang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div> 

      {/* Form */}
      <div className="bg-[url(/images/buban_xia.png)] bg-no-repeat bg-[length:100%_100%] -mt-[30px] rounded-xl p-5 shadow pb-[150px] pt-[60px] mt-3 relative z-10">
         <div className="px-4  space-y-1">

          {/* 本人姓名 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_benren.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold w-[80px] flex-shrink-0">本人姓名：</span>
            <input
              className="flex-1 bg-[#f5f5f5] rounded-lg px-3 py-2.5 text-xs text-[#333] focus:outline-none focus:ring-2 focus:ring-[#c4161c]/20"
              placeholder="请输入本人姓名"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          {/* 是否团队长 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_ruzhu.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold w-[80px] flex-shrink-0">是否团队长：</span>
            <div className="flex-1 relative">
              <select
                className="w-full bg-[#f5f5f5] rounded-lg px-3 py-2.5 text-xs text-[#333] appearance-none focus:outline-none focus:ring-2 focus:ring-[#c4161c]/20"
                value={isTeamLeader}
                onChange={e => setIsTeamLeader(e.target.value)}
              >
                <option value="" disabled>请选择</option>
                <option value="是">是</option>
                <option value="否">否</option>
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </div>
          </div>

          {/* 购买房产 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_gouwu.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold w-[80px] flex-shrink-0">购买房产：</span>
            <input
              className="flex-1 bg-[#f5f5f5] rounded-lg px-3 py-2.5 text-xs text-[#333] focus:outline-none focus:ring-2 focus:ring-[#c4161c]/20"
              placeholder="请输入购买的房产信息"
              value={propertyInfo}
              onChange={e => setPropertyInfo(e.target.value)}
            />
          </div>

          {/* 是否需要重新激活入住 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_jihuo.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold flex-shrink-0">是否需要重新激活入住：</span>
            <div className="flex items-center gap-4 ml-auto">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${needReactivation ? 'border-[#c4161c]' : 'border-gray-300'}`}>
                  {needReactivation && <span className="w-2.5 h-2.5 rounded-full bg-[#c4161c]" />}
                </span>
                <span className="text-xs text-[#333]">是</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => setNeedReactivation(false)}>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!needReactivation ? 'border-[#c4161c]' : 'border-gray-300'}`}>
                  {!needReactivation && <span className="w-2.5 h-2.5 rounded-full bg-[#c4161c]" />}
                </span>
                <span className="text-xs text-[#333]">否</span>
              </label>
            </div>
          </div>

          {/* 是否需要申请房屋补偿 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_bucang.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold flex-shrink-0">是否需要申请房屋补偿：</span>
            <div className="flex items-center gap-4 ml-auto">
              <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => setNeedCompensation(true)}>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${needCompensation ? 'border-[#c4161c]' : 'border-gray-300'}`}>
                  {needCompensation && <span className="w-2.5 h-2.5 rounded-full bg-[#c4161c]" />}
                </span>
                <span className="text-xs text-[#333]">是</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => setNeedCompensation(false)}>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!needCompensation ? 'border-[#c4161c]' : 'border-gray-300'}`}>
                  {!needCompensation && <span className="w-2.5 h-2.5 rounded-full bg-[#c4161c]" />}
                </span>
                <span className="text-xs text-[#333]">否</span>
              </label>
            </div>
          </div>

          {/* 入住编号 */}
          <div className="flex items-center gap-3">
            <img src="/images/buban_bucang.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-xs font-bold w-[80px] flex-shrink-0">入住编号：</span>
            <input
              className="flex-1 bg-[#f5f5f5] rounded-lg px-3 py-2.5 text-xs text-[#999] focus:outline-none"
              value={houseNumber || '待分配'}
              readOnly
            />
          </div>
        </div>

          {/* Submit button */}
          <div className="px-4 mt-2 mb-3 text-center">
            <button onClick={handleSubmit} disabled={submitting} className="w-[300px] mx-auto">
              <img src="/images/buban_tijiap.png" alt="立即提交" className={`w-full ${submitting ? 'opacity-50' : ''}`} />
            </button>
          {/* View cert button */}
          {(certMovein || certImageUrl) && (
              <button
                onClick={() => { if (certMovein) setShowMovein(true); else if (certImageUrl) setShowCert(true); }}
                className="w-[300px] mx-auto"
              >
                <img src="/images/buban_ruzhuzheng.png" alt="" className="w-full" /> 
              </button>
          )}
          </div>

      </div>
      
      {/* Floor Type Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[90%] max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-center text-lg font-bold text-[#8b0000] py-4">户型详情</h3>
            {viewingType && resolveImg(viewingType.image_url) ? (
              <div className="px-4 mb-3">
                <img src={resolveImg(viewingType.image_url)!} alt={viewingType.name} className="w-full rounded-lg" />
              </div>
            ) : viewingType ? (
              <div className="px-4 mb-3">
                <div className="w-full aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">户型图待上传</div>
              </div>
            ) : null}
            <div className="px-4">
              <h4 className="font-bold text-sm text-gray-800 mb-2 border-b-2 border-[#8b0000] pb-1 inline-block">面积选择</h4>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {allFloorTypes.map(ft => {
                  const isAssigned = assignedFloor?.id === ft.id;
                  return (
                    <button key={ft.id} onClick={() => setSelectedView(ft.id)}
                      className={`p-3 rounded-lg border-2 text-center transition ${isAssigned ? 'border-[#c4161c] bg-[#fef5ec]' : selectedView === ft.id ? 'border-gray-300 bg-gray-50' : 'border-gray-200 bg-white'}`}>
                      <div className="font-bold text-sm text-gray-800">{ft.name}</div>
                      <div className="text-[#c4161c] font-bold text-sm mt-0.5">{ft.area}平</div>
                      {isAssigned && (
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <img src="/images/buban_shi.png" alt="" className="w-3 h-3" />
                          <span className="text-[10px] text-[#c4161c]">您的户型</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="border-t mt-4">
              <button onClick={() => setShowModal(false)} className="w-full py-4 text-center text-gray-600 text-sm font-medium">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* Movein Cert Modal */}
      {showMovein && certMovein && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/70" onClick={() => setShowMovein(false)}>
          <div className="w-[95%] max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <img src={resolveImg(certMovein) || certMovein} alt="入住证明" className="w-full rounded-lg" />
          </div>
          <div className="w-[95%] max-w-md flex gap-3 mt-3" onClick={e => e.stopPropagation()}>
            <a href={resolveImg(certMovein) || certMovein} target="_blank" rel="noopener noreferrer" className="flex-1 py-3 bg-[#c4161c] rounded-lg text-center text-white text-sm font-medium">查看大图</a>
            <button onClick={() => setShowMovein(false)} className="flex-1 py-3 bg-white rounded-lg text-center text-gray-600 text-sm font-medium">关闭</button>
          </div>
        </div>
      )}

      {/* Cert Image Modal */}
      {showCert && certImageUrl && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/70" onClick={() => setShowCert(false)}>
          <div className="w-[95%] max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <img src={resolveImg(certImageUrl) || certImageUrl} alt="不动产权证书" className="w-full rounded-lg" />
          </div>
          <div className="w-[95%] max-w-md flex gap-3 mt-3" onClick={e => e.stopPropagation()}>
            <a href={resolveImg(certImageUrl) || certImageUrl} target="_blank" rel="noopener noreferrer" className="flex-1 py-3 bg-[#c4161c] rounded-lg text-center text-white text-sm font-medium">查看大图</a>
            <button onClick={() => setShowCert(false)} className="flex-1 py-3 bg-white rounded-lg text-center text-gray-600 text-sm font-medium">关闭</button>
          </div>
        </div>
      )}
    </div>
  );
}
